import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { BrandLogo } from '../components/BrandLogo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatApiError } from '../lib/api';
import { Shield, LogIn, UserPlus, KeyRound, Eye, EyeOff } from 'lucide-react';

function PasswordInput({ name, placeholder, testId, minLength, ...props }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        data-testid={testId}
        name={name}
        type={show ? 'text' : 'password'}
        required
        minLength={minLength}
        placeholder={placeholder}
        className="mt-1 font-body text-sm pr-10"
        {...props}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setShow(!show)}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-ink/30 hover:text-ink/60 transition-colors"
        data-testid={`${testId}-toggle`}
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

export default function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(useLocation().search);
  const nextPath = searchParams.get('next');
  const idleReason = searchParams.get('reason') === 'idle';
  const ownerFlow = Boolean(nextPath && /^\/(claim|audit|b)\//.test(nextPath));
  const [accountType, setAccountType] = useState('business_owner');

  const afterAuth = async (data, fallbackType = null) => {
    if (nextPath && nextPath.startsWith('/')) { navigate(nextPath); return; }
    try {
      const { data: claims } = await api.get('/claims/mine');
      if ((claims || []).length > 0) { navigate('/my-business'); return; }
    } catch (e) { console.warn('claims lookup failed', e); }
    const type = data?.account_type ?? fallbackType;
    navigate(type === 'business_owner' ? '/search?intent=claim' : '/');
  };
  const [error, setError] = useState(idleReason ? 'You were signed out after 30 minutes of inactivity. Please sign in again to continue.' : '');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    setIsLoading(true);
    const form = new FormData(e.target);
    try {
      const data = await login(form.get('email'), form.get('password'));
      await afterAuth(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    setIsLoading(true);
    const form = new FormData(e.target);
    try {
      const data = await register(form.get('email'), form.get('password'), form.get('name'), form.get('organisation'), accountType);
      setSuccess(data?.message || 'Thank you — your account has been created. Please check your inbox to confirm your email address before signing in.');
      // Small delay to show success message before redirect
      setTimeout(() => afterAuth(data, accountType), 1200);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(formatApiError(detail));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-page-bg relative overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1764010331153-6d79b3117522?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxzdG9ja3RvbiUyMG9uJTIwdGVlc3xlbnwwfHx8fDE3NzYxMTc2Njl8MA&ixlib=rb-4.1.0&q=85" 
          alt="" 
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-page-bg/80 to-page-bg" />
      </div>
      
      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="text-center mb-8 animate-fade-in">
          <div className="flex items-center justify-center mb-6">
            <BrandLogo className="h-6 sm:h-7 w-auto brightness-0" testId="login-brand" />
          </div>
          <div className="flex justify-center mb-4">
            <span className="inline-flex items-center gap-2 bg-brand-orange/10 text-brand-orange px-3 py-1 rounded-[4px]">
              <Shield className="w-4 h-4" strokeWidth={1.5} />
              <span className="font-body text-xs uppercase tracking-[0.1em]">Secure Access</span>
            </span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold tracking-tight text-ink">
            {ownerFlow ? 'Claim your business on Adoptic' : 'Sign in to Adoptic'}
          </h1>
          <p className="font-body text-base text-ink/70 font-medium mt-2" data-testid="login-subtitle">
            {ownerFlow ? 'Step 1 of 5 · Sign in or create a free owner account to verify ownership' : 'Tees Valley deployment'}
          </p>
          <p className="font-body text-sm text-ink/60 mt-2 leading-relaxed max-w-sm mx-auto">
            Access your postcode-level business intelligence workspace. Data is grounded in Ofcom Connected Nations, Companies House (OGL v3.0), and ONS IMD.
          </p>
        </div>

        <div className="glass-panel p-6 animate-fade-in" style={{ animationDelay: '0.1s' }}>
          <Tabs defaultValue={ownerFlow ? 'register' : 'login'}>
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-ink/5">
              <TabsTrigger value="login" data-testid="login-tab" className="font-body text-sm data-[state=active]:bg-white data-[state=active]:text-ink">
                <LogIn className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Sign In
              </TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab" className="font-body text-sm data-[state=active]:bg-white data-[state=active]:text-ink">
                <UserPlus className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Create Account
              </TabsTrigger>
            </TabsList>

            {error && (
              <div data-testid="auth-error" className="bg-status-red/10 border border-status-red/20 text-status-red px-3 py-2 rounded-[4px] text-xs font-body mb-4">
                {error}
              </div>
            )}
            {success && (
              <div data-testid="auth-success" className="bg-status-green/10 border border-status-green/20 text-status-green px-3 py-2 rounded-[4px] text-xs font-body mb-4">
                {success}
              </div>
            )}

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label className="font-body text-xs uppercase tracking-[0.1em] text-ink/70">Work Email</Label>
                  <Input data-testid="login-email" name="email" type="email" required placeholder="name@organisation.gov.uk" className="mt-1 font-body text-sm" />
                </div>
                <div>
                  <Label className="font-body text-xs uppercase tracking-[0.1em] text-ink/70">Password</Label>
                  <PasswordInput name="password" testId="login-password" placeholder="Enter your password" />
                </div>
                <p className="font-body text-[11px] text-ink/50 leading-relaxed text-center" data-testid="login-consent">
                  By continuing, you agree to our <Link to="/terms" className="underline hover:text-ink">Terms of Service</Link>, <Link to="/privacy" className="underline hover:text-ink">Privacy Policy</Link> and <Link to="/cookies" className="underline hover:text-ink">Cookie Use</Link>.
                </p>
                <Button data-testid="login-submit" type="submit" disabled={isLoading} className="w-full bg-brand-orange text-white hover:bg-brand-orange/90 font-body font-medium rounded-[4px] transition-all hover:-translate-y-[1px]">
                  {isLoading ? 'Signing in…' : 'Sign In'}
                </Button>
                <div className="text-center pt-1">
                  <a href="/forgot-password" className="inline-flex items-center gap-1 font-body text-xs text-ink/40 hover:text-brand-orange transition-colors" data-testid="forgot-password-link">
                    <KeyRound className="w-3 h-3" /> Forgot your password?
                  </a>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <p className="font-body text-sm text-ink/60 mb-4 leading-relaxed">
                Business owners: create a free account, verify ownership and your business is ranked first when anyone searches your sector, category, name or postcode. Council and Buyer/Partner accounts are reviewed within 1 working day.
              </p>
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label className="font-body text-xs uppercase tracking-[0.1em] text-ink/70">I am a</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1" data-testid="account-type-picker">
                    {[['business_owner', 'Business owner'], ['council', 'Council / LA'], ['buyer_partner', 'Buyers / Partners']].map(([v, l]) => (
                      <button key={v} type="button" onClick={() => setAccountType(v)} data-testid={`account-type-${v}`}
                        className={`px-2 py-2 rounded-[6px] border font-body text-xs transition-colors ${accountType === v ? 'bg-ink text-white border-ink' : 'bg-white text-ink/70 border-ink/15 hover:border-ink/40'}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="font-body text-xs uppercase tracking-[0.1em] text-ink/70">Full Name</Label>
                  <Input data-testid="register-name" name="name" required placeholder="Your full name" className="mt-1 font-body text-sm" />
                </div>
                <div>
                  <Label className="font-body text-xs uppercase tracking-[0.1em] text-ink/70">Organisation</Label>
                  <Input data-testid="register-org" name="organisation" placeholder={accountType === 'business_owner' ? 'Your business name' : accountType === 'buyer_partner' ? 'Your company or organisation' : 'e.g. Stockton-on-Tees Borough Council'} className="mt-1 font-body text-sm" />
                </div>
                <div>
                  <Label className="font-body text-xs uppercase tracking-[0.1em] text-ink/70">Work Email</Label>
                  <Input data-testid="register-email" name="email" type="email" required placeholder="name@organisation.gov.uk" className="mt-1 font-body text-sm" />
                </div>
                <div>
                  <Label className="font-body text-xs uppercase tracking-[0.1em] text-ink/70">Create Password</Label>
                  <PasswordInput name="password" testId="register-password" minLength={8} placeholder="At least 8 characters (upper, lower, digit, special)" />
                </div>
                <p className="font-body text-[11px] text-ink/50 leading-relaxed text-center" data-testid="register-consent">
                  By continuing, you agree to our <Link to="/terms" className="underline hover:text-ink">Terms of Service</Link>, <Link to="/privacy" className="underline hover:text-ink">Privacy Policy</Link> and <Link to="/cookies" className="underline hover:text-ink">Cookie Use</Link>.
                </p>
                <Button data-testid="register-submit" type="submit" disabled={isLoading} className="w-full bg-brand-orange text-white hover:bg-brand-orange/90 font-body font-medium rounded-[4px] transition-all hover:-translate-y-[1px]">
                  {isLoading ? 'Creating account…' : 'Request Access'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        <p className="text-center font-body text-xs text-ink/40 mt-6">
          Data sources: Companies House (OGL v3.0) · ONS IDBR · Ofcom Connected Nations<br />
          <a href="/privacy" className="underline">Privacy Policy</a> · &copy; {new Date().getFullYear()} Nexintell Limited
        </p>
      </div>
    </div>
  );
}
